/**
 * Brand configuration system.
 * Switches behavior based on NEXT_PUBLIC_BRAND env variable.
 * Valid values: 'sais' | 'happa'
 */

export type BrandId = 'sais' | 'happa'

export interface BrandColors {
  primary: {
    50: string
    100: string
    200: string
    300: string
    400: string
    500: string
    600: string
    700: string
    800: string
    900: string
  }
  yellow: {
    50: string
    100: string
    200: string
    300: string
    400: string
    500: string
    600: string
    700: string
    800: string
    900: string
  }
  secondary: {
    main: string
    light: string
    dark: string
  }
}

export interface BrandFonts {
  heading: string
  body: string
  button: string
}

export interface BrandSizes {
  headerLogoHeight: { xs: string; sm: string }
  headerLogoHeightMinimized: { xs: string; sm: string }
  headerHeight: { xs: string; sm: string }
}

export interface BrandFeatures {
  /** Whether advertisements module is enabled */
  advertisements: boolean
  /** Whether chat module is enabled */
  chat: boolean
  /** Whether bookings module is enabled */
  bookings: boolean
  /** Whether stats/analytics dashboard is enabled */
  stats: boolean
}

export interface BrandMeta {
  name: string
  shortName: string
  gtmId: string
}

export interface BrandConfig {
  id: BrandId
  meta: BrandMeta
  colors: BrandColors
  fonts: BrandFonts
  sizes: BrandSizes
  features: BrandFeatures
  /** User id of the support inbox account */
  supportUserId: number
}

// ─── SAIS Configuration ────────────────────────────────────────────────────────

const saisConfig: BrandConfig = {
  id: 'sais',
  meta: {
    name: 'SAIS Accommodation',
    shortName: 'SAIS',
    gtmId: process.env.NEXT_PUBLIC_GTM_ID || ''
  },
  colors: {
    primary: {
      50: '#E6ECF9',
      100: '#C0CEF0',
      200: '#97ACE6',
      300: '#6D89DC',
      400: '#4E6FD4',
      500: '#002F97',
      600: '#002A8A',
      700: '#002378',
      800: '#001D66',
      900: '#00124A'
    },
    yellow: {
      50: '#FFF7E0',
      100: '#FFE9B3',
      200: '#FFDB80',
      300: '#FFCD4D',
      400: '#FFC226',
      500: '#F8B90D',
      600: '#E3A80C',
      700: '#C9920A',
      800: '#AF7C08',
      900: '#855D05'
    },
    secondary: {
      main: '#FFC107',
      light: '#FFF8E1',
      dark: '#E3A80C'
    }
  },
  fonts: {
    heading: 'var(--heading-font)',
    body: 'var(--base-font)',
    button: 'var(--heading-font)'
  },
  sizes: {
    headerLogoHeight: { xs: '5.4rem', sm: '12.8rem' },
    headerLogoHeightMinimized: { xs: '5.4rem', sm: '8rem' },
    headerHeight: { xs: '9.6rem', sm: '17.6rem' }
  },
  features: {
    advertisements: true,
    chat: true,
    bookings: true,
    stats: true
  },
  supportUserId: 504370
}

// ─── HAPPA Configuration ───────────────────────────────────────────────────────

const happaConfig: BrandConfig = {
  id: 'happa',
  meta: {
    name: 'Happa',
    shortName: 'Happa',
    gtmId: process.env.NEXT_PUBLIC_GTM_ID || ''
  },
  colors: {
    primary: {
      50: '#E7EAFD',
      100: '#C6CEFB',
      200: '#97A5F7',
      300: '#677CF3',
      400: '#4B63F1',
      500: '#112FDF',
      600: '#112EDA',
      700: '#0E26B4',
      800: '#0B1E8E',
      900: '#07145F'
    },
    yellow: {
      50: '#FAFBEA',
      100: '#F3F4CD',
      200: '#EAEBA2',
      300: '#E0E278',
      400: '#DADD5F',
      500: '#D5D847',
      600: '#BEC229',
      700: '#9DA022',
      800: '#7C7E1B',
      900: '#535412'
    },
    secondary: {
      main: '#FFC107',
      light: '#FFF8E1',
      dark: '#E3A80C'
    }
  },
  fonts: {
    heading: 'var(--heading-font)',
    body: 'var(--base-font)',
    button: 'var(--button-font)'
  },
  sizes: {
    headerLogoHeight: { xs: '4rem', sm: '8.4rem' },
    headerLogoHeightMinimized: { xs: '4rem', sm: '4.6rem' },
    headerHeight: { xs: '9.6rem', sm: '17.6rem' }
  },
  features: {
    advertisements: true,
    chat: true,
    bookings: true,
    stats: true
  },
  supportUserId: 505159
}

// ─── Brand Resolution ──────────────────────────────────────────────────────────

const brands: Record<BrandId, BrandConfig> = {
  sais: saisConfig,
  happa: happaConfig
}

export const BRAND_ID: BrandId = (process.env.NEXT_PUBLIC_BRAND as BrandId) || 'sais'

export const brand: BrandConfig = brands[BRAND_ID] ?? brands.sais

export const isSais = BRAND_ID === 'sais'
export const isHappa = BRAND_ID === 'happa'

/**
 * Kill switch for the orders/payments/boost purchasing system on Happa.
 * Set NEXT_PUBLIC_PAYMENTS_ENABLED=false to hide and disable it without a deploy of code changes.
 */
export const paymentsEnabled = isHappa && process.env.NEXT_PUBLIC_PAYMENTS_ENABLED !== 'false'
